import { FC } from 'react';
import { FaLock, FaShieldAlt, FaUsers } from 'react-icons/fa';
import { HiArrowRight, HiPlus } from 'react-icons/hi';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/lib';
import RouteConstants from '@/features/router/RouteConstants';
import type { EmailCampaignGate } from '../hooks/useEmailCampaignGate';

type EmailCampaignGateCardProps = {
  variant: Exclude<EmailCampaignGate, 'ready'>;
};

export const EmailCampaignGateCard: FC<EmailCampaignGateCardProps> = ({ variant }) => {
  const { t } = useTranslation('campaign');
  const history = useHistory();

  const handlePrimaryAction = () => {
    if (variant === 'connect-mailtrap') {
      history.push(RouteConstants.destinations);
      return;
    }

    if (variant === 'no-audience') {
      history.push(RouteConstants.audiencesCreate);
      return;
    }

    if (variant === 'domain-not-verified') {
      history.push(RouteConstants.emailDomains);
      return;
    }

    history.push(RouteConstants.emailDomains, { openAddForm: true });
  };

  const isConnectVariant = variant === 'connect-mailtrap';
  const isNoAudienceVariant = variant === 'no-audience';
  const isDomainNotVerifiedVariant = variant === 'domain-not-verified';

  return (
    <section className='bg-surface flex flex-col items-center rounded-3xl border border-secondary-border px-6 py-16 text-center shadow-sm md:px-10 md:py-20'>
      <div
        className={`flex size-[74px] items-center justify-center rounded-2xl ${
          isConnectVariant ? 'bg-orange-50 text-orange-600' : isNoAudienceVariant ? 'bg-violet-50 text-violet-700' : 'bg-blue-50 text-blue-700'
        }`}
      >
        {isConnectVariant ? <FaLock className='size-8' /> : isNoAudienceVariant ? <FaUsers className='size-8' /> : <FaShieldAlt className='size-8' />}
      </div>

      <h2 className='mt-6 text-2xl font-bold text-slate-900 md:text-[23px]'>
        {t(
          isConnectVariant
            ? 'emailWorkspace.gates.connectTitle'
            : isNoAudienceVariant
              ? 'emailWorkspace.gates.noAudienceTitle'
              : isDomainNotVerifiedVariant
                ? 'emailWorkspace.gates.domainNotVerifiedTitle'
                : 'emailWorkspace.gates.domainTitle'
        )}
      </h2>

      <p className='text-secondary-600 mt-2 max-w-xl text-[15px] leading-relaxed'>
        {t(
          isConnectVariant
            ? 'emailWorkspace.gates.connectDescription'
            : isNoAudienceVariant
              ? 'emailWorkspace.gates.noAudienceDescription'
              : isDomainNotVerifiedVariant
                ? 'emailWorkspace.gates.domainNotVerifiedDescription'
                : 'emailWorkspace.gates.domainDescription'
        )}
      </p>

      <Button intent='primary' size='md' className='mt-6 inline-flex items-center gap-2 px-5 py-3' onClick={handlePrimaryAction}>
        {isConnectVariant ? (
          <>
            {t('emailWorkspace.gates.goToDestinations')}
            <HiArrowRight className='size-4' />
          </>
        ) : isNoAudienceVariant ? (
          <>
            <HiPlus className='size-4' />
            {t('emailWorkspace.gates.noAudienceAction')}
          </>
        ) : isDomainNotVerifiedVariant ? (
          <>
            {t('emailWorkspace.gates.verifySendingDomain')}
            <HiArrowRight className='size-4' />
          </>
        ) : (
          <>
            <HiPlus className='size-4' />
            {t('emailWorkspace.gates.addSendingDomain')}
          </>
        )}
      </Button>
    </section>
  );
};
